import type { StreamPoint } from '../workoutStream';

export type ComparisonMetric = 'power' | 'heart_rate' | 'cadence' | 'speed' | 'altitude';
export type ComparisonAlignment = 'time' | 'distance';

export interface AlignedPoint {
  x: number;
  valueA: number | null;
  valueB: number | null;
}

const METRIC_GETTERS: Record<ComparisonMetric, (p: StreamPoint) => number | null> = {
  power: p => p.power,
  heart_rate: p => p.heartRate,
  cadence: p => p.cadence,
  speed: p => p.speed,
  altitude: p => p.altitude,
};

/** Haversine distance in metres between two semicircle-encoded FIT coords. */
function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toDeg = (v: number) => v * (180 / Math.pow(2, 31));
  const la1 = toDeg(lat1);
  const lo1 = toDeg(lon1);
  const la2 = toDeg(lat2);
  const lo2 = toDeg(lon2);
  const R = 6371000;
  const dLat = ((la2 - la1) * Math.PI) / 180;
  const dLon = ((lo2 - lo1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((la1 * Math.PI) / 180) * Math.cos((la2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Build cumulative distance (metres) per stream point. */
export function buildCumulativeDistance(stream: StreamPoint[]): number[] {
  const dist: number[] = [0];
  for (let i = 1; i < stream.length; i++) {
    let step = 0;
    const prev = stream[i - 1];
    const cur = stream[i];
    if (
      prev.positionLat != null &&
      prev.positionLong != null &&
      cur.positionLat != null &&
      cur.positionLong != null
    ) {
      step = haversineM(prev.positionLat, prev.positionLong, cur.positionLat, cur.positionLong);
    } else if (prev.speed != null && cur.speed != null && cur.timeSeconds > prev.timeSeconds) {
      step = ((prev.speed + cur.speed) / 2) * (cur.timeSeconds - prev.timeSeconds) * (1000 / 3600);
    }
    dist.push(dist[i - 1] + step);
  }
  return dist;
}

/** Slice stream by elapsed seconds [startSec, endSec]. */
export function sliceStreamByTime(stream: StreamPoint[], startSec: number, endSec: number): StreamPoint[] {
  return stream.filter(p => p.timeSeconds >= startSec && p.timeSeconds <= endSec);
}

/** Downsample aligned series to maxPoints using uniform stride. */
export function downsampleSeries<T>(points: T[], maxPoints = 2000): T[] {
  if (points.length <= maxPoints) return points;
  const step = points.length / maxPoints;
  const out: T[] = [];
  for (let i = 0; i < maxPoints; i++) {
    out.push(points[Math.floor(i * step)]);
  }
  return out;
}

function getMetricValue(p: StreamPoint, metric: ComparisonMetric): number | null {
  return METRIC_GETTERS[metric](p);
}

/** Linear interpolate metric value at target x from sorted stream. */
function sampleAtX(stream: StreamPoint[], xArr: number[], metric: ComparisonMetric, targetX: number): number | null {
  if (!stream.length) return null;
  if (targetX <= xArr[0]) return getMetricValue(stream[0], metric);
  if (targetX >= xArr[xArr.length - 1]) return getMetricValue(stream[stream.length - 1], metric);

  let lo = 0;
  let hi = xArr.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (xArr[mid] <= targetX) lo = mid;
    else hi = mid;
  }
  const v0 = getMetricValue(stream[lo], metric);
  const v1 = getMetricValue(stream[hi], metric);
  const t = (targetX - xArr[lo]) / (xArr[hi] - xArr[lo] || 1);
  if (v0 != null && v1 != null) return v0 + t * (v1 - v0);
  return v0 ?? v1;
}

/**
 * Align two streams for overlay chart.
 * x-axis: elapsed seconds (time) or cumulative metres (distance).
 */
export function alignStreams(
  streamA: StreamPoint[],
  streamB: StreamPoint[],
  metric: ComparisonMetric,
  alignment: ComparisonAlignment,
  maxPoints = 2000,
): AlignedPoint[] {
  if (!streamA.length && !streamB.length) return [];
  if (!streamA.length || !streamB.length) {
    const single = streamA.length ? streamA : streamB;
    const xArr =
      alignment === 'distance' ? buildCumulativeDistance(single) : single.map(p => p.timeSeconds);
    return downsampleSeries(
      single.map((p, i) => ({
        x: xArr[i],
        valueA: streamA.length ? getMetricValue(p, metric) : null,
        valueB: streamB.length ? getMetricValue(p, metric) : null,
      })),
      maxPoints,
    );
  }

  const xA = alignment === 'distance' ? buildCumulativeDistance(streamA) : streamA.map(p => p.timeSeconds);
  const xB = alignment === 'distance' ? buildCumulativeDistance(streamB) : streamB.map(p => p.timeSeconds);

  const maxX = Math.max(xA[xA.length - 1] ?? 0, xB[xB.length - 1] ?? 0);
  const minX = 0;
  const sampleCount = Math.min(maxPoints, Math.max(streamA.length, streamB.length));
  const step = sampleCount > 1 ? (maxX - minX) / (sampleCount - 1) : 0;

  const aligned: AlignedPoint[] = [];
  for (let i = 0; i < sampleCount; i++) {
    const x = minX + i * step;
    aligned.push({
      x,
      valueA: sampleAtX(streamA, xA, metric, x),
      valueB: sampleAtX(streamB, xB, metric, x),
    });
  }
  return aligned;
}

/** Normalize both streams to 0–100% progress for unequal durations. */
export function alignByNormalizedProgress(
  streamA: StreamPoint[],
  streamB: StreamPoint[],
  metric: ComparisonMetric,
  maxPoints = 2000,
): AlignedPoint[] {
  const sampleCount = maxPoints;
  const aligned: AlignedPoint[] = [];
  for (let i = 0; i < sampleCount; i++) {
    const frac = sampleCount > 1 ? i / (sampleCount - 1) : 0;
    const idxA = Math.min(streamA.length - 1, Math.round(frac * (streamA.length - 1)));
    const idxB = Math.min(streamB.length - 1, Math.round(frac * (streamB.length - 1)));
    aligned.push({
      x: frac * 100,
      valueA: streamA.length ? getMetricValue(streamA[idxA], metric) : null,
      valueB: streamB.length ? getMetricValue(streamB[idxB], metric) : null,
    });
  }
  return aligned;
}
